import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/services/core/apiClient';
import { useToast } from '@/hooks/use-toast';

interface CreateClinicUserGroupPayload {
  groupName: string;
  stateId?: number;
  clinicIds?: number[];
  userIds: number[];
}

interface EditClinicUserGroupPayload {
  clinicUserGroupId: number;
  groupName: string;
  addUserIds: number[];
  removeUserIds: number[];
  addClinicIds?: number[];
  removeClinicIds?: number[];
}

export interface ClinicUserGroupDetails {
  clinicUserGroupId: number;
  groupName: string;
  totalUserCount: number;
  createdAt: string;
  users: { userId: number; userName: string; appUserName: string | null; userType?: string }[];
  clinics: { clinicId: number; clinicName: string }[];
  stateId?: number;
}

export const useClinicUserGroup = () => {
  const { user, token } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const createClinicUserGroup = async (payload: CreateClinicUserGroupPayload) => {
    if (!user?.id || !token) {
      throw new Error('User not authenticated');
    }

    setLoading(true);
    try {
      const endpoint = `/api/ngpep/${user.id}/clinic-user-groups`;
      const response = await apiClient.post(endpoint, payload, token);

      if (!response.success) {
        throw new Error(response.error || 'Failed to create clinic user group');
      }

      toast({
        title: "Clinic User Group Created",
        description: `Successfully created clinic user group "${payload.groupName}".`,
      });

      return response.data;
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || 'Failed to create clinic user group',
        variant: "destructive",
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const editClinicUserGroup = async (clinicUserGroupId: number, payload: EditClinicUserGroupPayload) => {
    if (!user?.id || !token) {
      throw new Error('User not authenticated');
    }

    setLoading(true);
    try {
      const endpoint = `/api/ngpep/${user.id}/clinic-user-groups/${clinicUserGroupId}`;
      const response = await apiClient.put(endpoint, payload, token);

      if (!response.success) {
        throw new Error(response.error || 'Failed to update clinic user group');
      }

      toast({
        title: "Clinic User Group Updated",
        description: `Successfully updated clinic user group "${payload.groupName}".`,
      });

      return response.data;
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || 'Failed to update clinic user group',
        variant: "destructive",
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getClinicUserGroupDetails = async (clinicUserGroupId: number): Promise<ClinicUserGroupDetails | null> => {
    if (!user?.id || !token) {
      console.warn('[getClinicUserGroupDetails] Not authenticated, skipping API call');
      return null;
    }

    setLoading(true);
    try {
      const endpoint = `/api/ngpep/${user.id}/clinic-user-groups/${clinicUserGroupId}/details`;
      console.log('[getClinicUserGroupDetails] Calling:', endpoint);
      const response = await apiClient.get<any>(endpoint, token);
      console.log('[getClinicUserGroupDetails] Response:', JSON.stringify(response));

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch clinic user group details');
      }

      const apiData = response.data;
      
      // Handle different response structures
      // Could be { data: {...}, success: true } or directly the details object
      if (apiData?.data && typeof apiData.data === 'object' && !Array.isArray(apiData.data)) {
        return apiData.data as ClinicUserGroupDetails;
      }
      
      // If the response.data itself is the details object
      if (apiData?.clinicUserGroupId) {
        return apiData as ClinicUserGroupDetails;
      }

      console.warn('[getClinicUserGroupDetails] Unexpected response structure:', apiData);
      throw new Error('Unexpected response format');
    } catch (error: any) {
      console.error('[getClinicUserGroupDetails] Error:', error);
      toast({
        title: "Error",
        description: error.message || 'Failed to fetch clinic user group details',
        variant: "destructive",
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteClinicUserGroup = async (clinicUserGroupId: number, groupName: string) => {
    console.log('[deleteClinicUserGroup] Called with:', { clinicUserGroupId, groupName, userId: user?.id, hasToken: !!token });
    if (!user?.id || !token) {
      console.error('[deleteClinicUserGroup] Not authenticated');
      throw new Error('User not authenticated');
    }

    setLoading(true);
    try {
      const endpoint = `/api/ngpep/${user.id}/clinic-user-groups/${clinicUserGroupId}`;
      const response = await apiClient.delete(endpoint, token);

      if (!response.success) {
        throw new Error(response.error || 'Failed to delete clinic user group');
      }

      toast({
        title: "Clinic User Group Deleted",
        description: `Successfully deleted clinic user group "${groupName}".`,
      });

      return response.data;
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || 'Failed to delete clinic user group',
        variant: "destructive",
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { createClinicUserGroup, editClinicUserGroup, getClinicUserGroupDetails, deleteClinicUserGroup, loading };
};
